import { useEffect, useMemo, useState } from 'react'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { Adddata, db, uploadCommerceLogo } from '../../data'
import { ToastError, ToastSucces } from '../tools'
import editIco from '../../assets/images/editIco.png'
import searchIco from '../../assets/images/searchIco.png'
import peabody from '../../assets/images/peab.png'
import goldImg from '../../assets/images/goldImg.png'
import { defaultOrderAdjustments, resolveOrderAdjustments } from '../../utils/orderAdjustments'

const baseCommerceIds = ['goldmund', 'escorial']

const commerceLogos = {
    peabody,
    goldmund: goldImg,
}

const emptyCommerce = {
    id: '',
    displayName: '',
    legalName: '',
    taxId: '',
    warehouseCode: '',
    documentLabel: '',
    documentPrefix: '',
    primaryLogoKey: '',
    secondaryLogoKey: '',
    primaryLogoUrl: '',
    secondaryLogoUrl: '',
    proformaValidityNote: '',
    legalNote: '',
    conditionsNote: '',
    bankAccounts: [],
    orderFormConfig: {
        requiresFinalValidation: false,
        orderAdjustments: [],
        mupCategories: [],
        summary: {
            ivaLabel: '',
        },
    },
}

const defaultOrderFormConfig = {
    requiresFinalValidation: false,
    orderAdjustments: defaultOrderAdjustments,
    mupCategories: [
        { value: 'Calef', label: 'Calef' },
        { value: 'Venti', label: 'Venti' },
        { value: 'Pad', label: 'Pad' },
    ],
    summary: {
        ivaLabel: 'IVA 21%',
    },
}

const parseBankAccounts = (value) => {
    if (!value.trim()) return []

    return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const [bco = '', cc = '', cbu = ''] = line.split('|').map((part) => part.trim())
            return { bco, cc, cbu }
        })
        .filter((account) => account.bco)
}

const stringifyBankAccounts = (accounts = []) =>
    accounts.map((account) => [account.bco, account.cc, account.cbu].join(' | ')).join('\n')

const parseLines = (value) =>
    value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)

const normalizeCommerce = (commerceId, data = {}) => {
    const orderFormFallback = defaultOrderFormConfig

    return {
        ...emptyCommerce,
        ...data,
        id: commerceId,
        bankAccounts: Array.isArray(data.bankAccounts) ? data.bankAccounts : [],
        orderFormConfig: {
            ...orderFormFallback,
            ...(data.orderFormConfig || {}),
            requiresFinalValidation: typeof data.orderFormConfig?.requiresFinalValidation === 'boolean'
                ? data.orderFormConfig.requiresFinalValidation
                : orderFormFallback.requiresFinalValidation,
            orderAdjustments: resolveOrderAdjustments({
                ...orderFormFallback,
                ...(data.orderFormConfig || {}),
            }),
            mupCategories: Array.isArray(data.orderFormConfig?.mupCategories)
                ? data.orderFormConfig.mupCategories
                : orderFormFallback.mupCategories,
            summary: {
                ...orderFormFallback.summary,
                ...(data.orderFormConfig?.summary || {}),
            },
        },
    }
}

const CommercePreview = ({ commerce }) => {
    const primaryLogo = commerce.primaryLogoUrl || commerceLogos[commerce.primaryLogoKey]
    const secondaryLogo = commerce.secondaryLogoUrl || commerceLogos[commerce.secondaryLogoKey]
    const documentLabel = commerce.documentLabel || 'Documento'
    const documentPrefix = commerce.documentPrefix || ''
    const sampleDocumentNumber = `${documentPrefix}000123`
    const sampleBanks = commerce.bankAccounts.slice(0, 2)
    const sampleRows = [
        { articulo: 'SKU-001', descripcion: 'PRODUCTO 1', cant: 12, pvp: '$ 49.999,00', pvpSinIva: '$ 41.321,49', mup: 65, costo: '$ 25.043,33', total: '$ 300.519,91' },
        { articulo: 'SKU-002', descripcion: 'PRODUCTO 2', cant: 12, pvp: '$ 139.999,00', pvpSinIva: '$ 115.701,65', mup: 65, costo: '$ 70.122,21', total: '$ 841.466,57' },
        { articulo: 'SKU-003', descripcion: 'PRODUCTO 3', cant: 12, pvp: '$ 139.999,00', pvpSinIva: '$ 115.701,65', mup: 65, costo: '$ 70.122,21', total: '$ 841.466,57' },
        { articulo: 'SKU-004', descripcion: 'PRODUCTO 4', cant: 12, pvp: '$ 64.999,00', pvpSinIva: '$ 53.718,18', mup: 65, costo: '$ 32.556,47', total: '$ 390.677,69' },
        { articulo: 'SKU-005', descripcion: 'PRODUCTO 5', cant: 5, pvp: '$ 149.999,00', pvpSinIva: '$ 123.966,12', mup: 65, costo: '$ 75.130,98', total: '$ 375.654,90' },
    ]

    return (
        <div className='commercePreviewZone'>
            <div className='commercePreviewZoneHeader'>
                <span className='commercePreviewZoneBadge'>Previsualizacion</span>
                <span className='commercePreviewZoneText'>Mock del PDF para revisar branding, textos y estructura</span>
            </div>

        <div className='commercePreview'>
            <div className='commercePreviewHeader'>
                <div className='commercePreviewBrand'>
                    {primaryLogo && (
                        <img
                            src={primaryLogo}
                            alt={commerce.primaryLogoKey || 'Logo principal'}
                            className='commercePreviewLogo'
                        />
                    )}
                    {secondaryLogo ? (
                        <img
                            src={secondaryLogo}
                            alt={commerce.secondaryLogoKey || 'Logo secundario'}
                            className='commercePreviewLogo commercePreviewLogo--secondary'
                        />
                    ) : null}
                </div>
            </div>

            <div className='commercePreviewInfoBox'>
                <div className='commercePreviewColumns'>
                    <div className='commercePreviewColumn'>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>{documentLabel}</span>
                            <div className='commercePreviewFieldValue'>
                                <strong className='commercePreviewFieldLine'>{sampleDocumentNumber || '-'}</strong>
                                <span className='commercePreviewFieldLine'>28/07/2026</span>
                            </div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>ID Pedido</span>
                            <div className='commercePreviewFieldValue'>000123</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Num Pedido</span>
                            <div className='commercePreviewFieldValue'>OC-45678</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Vendedor</span>
                            <div className='commercePreviewFieldValue'>Perez, Juan</div>
                        </div>
                    </div>

                    <div className='commercePreviewColumn'>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Cliente</span>
                            <div className='commercePreviewFieldValue'>Cliente Demo S.A.</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Cuit</span>
                            <div className='commercePreviewFieldValue'>30-12345678-9</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Domicilio</span>
                            <div className='commercePreviewFieldValue'>Av. Siempre Viva 123, Buenos Aires</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Telefono</span>
                            <div className='commercePreviewFieldValue'>11 5555-5555</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Correo</span>
                            <div className='commercePreviewFieldValue'>cliente@demo.com</div>
                        </div>
                    </div>

                    <div className='commercePreviewColumn'>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Fecha</span>
                            <div className='commercePreviewFieldValue'>28/07/2026</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Pago</span>
                            <div className='commercePreviewFieldValue'>Contado Anticipado</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Dom. de Entrega</span>
                            <div className='commercePreviewFieldValue'>Deposito central - Buenos Aires</div>
                        </div>
                        <div className='commercePreviewField'>
                            <span className='commercePreviewFieldLabel'>Tel. Transporte</span>
                            <div className='commercePreviewFieldValue'>11 4444-4444</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='commercePreviewTableWrap'>
                <div className='commercePreviewWatermark'>SOLO PREVISUALIZACION</div>
                <table className='commercePreviewTable'>
                    <thead>
                        <tr>
                            <th>Articulo</th>
                            <th>Descripcion</th>
                            <th>Cant</th>
                            <th>PVP</th>
                            <th>PVP S/IVA</th>
                            <th>MUP</th>
                            <th>COSTO S/IVA</th>
                            <th>TOTAL S/IVA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sampleRows.map((row) => (
                            <tr key={row.articulo}>
                                <td>{row.articulo}</td>
                                <td>{row.descripcion}</td>
                                <td>{row.cant}</td>
                                <td>{row.pvp}</td>
                                <td>{row.pvpSinIva}</td>
                                <td>{row.mup}</td>
                                <td>{row.costo}</td>
                                <td>{row.total}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='commercePreviewSummary'>
                <div className='commercePreviewSummaryCol'>
                    <div><span>NETO</span><strong>$ 3.410.939,14</strong></div>
                    <div><span>DC. %</span><span>$ 0,00</span></div>
                    <div><span>DF. %</span><span>$ 0,00</span></div>
                    <div><span>DPP. %</span><span>$ 0,00</span></div>
                    <div><span>SUBTOTAL</span><strong>$ 3.410.939,14</strong></div>
                    <div><span>IVA 21 %</span><span>$ 716.297,22</span></div>
                </div>
                <div className='commercePreviewSummaryCol'>
                    <div><span>IIBB CABA</span><span>-</span><span>-</span></div>
                    <div><span>IIBB BS AS</span><span>-</span><span>-</span></div>
                    <div><span>IIBB TUC</span><span>-</span><span>-</span></div>
                    <div><span>IIBB CORD</span><span>$ 136.437,57</span><span>4.00%</span></div>
                    <div><span>IIBB ER</span><span>-</span><span>-</span></div>
                    <div><span>IIBB SFE</span><span>-</span><span>-</span></div>
                </div>
            </div>

            <div className='commercePreviewGrandTotal'>
                <span><strong>Unidades:</strong> 60</span>
                <span className='commercePreviewGrandTotalValue'><strong>TOTAL</strong> $ 4.263.673,93</span>
            </div>

            <div className='commercePreviewLegal'>
                <p className='commercePreviewParagraph'>
                    <strong>Vigencia de la proforma:</strong> {commerce.proformaValidityNote || 'Sin vigencia configurada.'}
                </p>
            </div>

            <div className='commercePreviewFooter'>
                <div className='commercePreviewFooterBox'>
                    <span className='commercePreviewSectionTitle'>CUENTAS PARA DEPOSITAR</span>
                    <p className='commercePreviewParagraph'>
                        Cuenta corriente en pesos a nombre de <strong>{commerce.legalName || '-'}</strong><br />
                        CUIT: <strong>{commerce.taxId || '-'}</strong>
                    </p>

                    {sampleBanks.length > 0 ? (
                        <div className='commercePreviewBankList'>
                            {sampleBanks.map((account) => (
                                <div key={`${account.bco}-${account.cc}-${account.cbu}`} className='commercePreviewBankRow'>
                                    <span className='commercePreviewBankName'>{account.bco}</span>
                                    <span><strong>C/C</strong> {account.cc || '-'}</span>
                                    <span><strong>CBU</strong> {account.cbu || '-'}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='commercePreviewParagraph'>No hay cuentas cargadas.</p>
                    )}
                </div>
            </div>

            <div className='commercePreviewLegal commercePreviewLegal--boxed'>
                <p className='commercePreviewParagraph'>
                    <strong>NOTA:</strong> {commerce.legalNote || 'Sin nota legal configurada.'}
                </p>
                <p className='commercePreviewParagraph'>
                    <strong>*</strong> {commerce.conditionsNote || 'Sin condiciones comerciales configuradas.'} <strong>*</strong>
                </p>
            </div>
        </div>
        </div>
    )
}

const normalizeText = (value) => {
    if (!value) return ''
    return value
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
}

export const ComerciosAdmin = () => {
    const [commerceMap, setCommerceMap] = useState({})
    const [selectedId, setSelectedId] = useState('goldmund')
    const [searchTerm, setSearchTerm] = useState('')
    const [editing, setEditing] = useState(false)
    const [formData, setFormData] = useState(emptyCommerce)
    const [bankAccountsText, setBankAccountsText] = useState('')
    const [adjustments, setAdjustments] = useState([])
    const [mupCategories, setMupCategories] = useState([])
    const [logoUploading, setLogoUploading] = useState({ primary: false, secondary: false })
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const commerceIds = useMemo(() => {
        const ids = new Set([...baseCommerceIds, ...Object.keys(commerceMap)])
        return [...ids]
    }, [commerceMap])

    const filteredCommerceIds = useMemo(() => {
        const search = normalizeText(searchTerm.trim())
        if (!search) return commerceIds

        return commerceIds.filter((commerceId) => {
            const commerce = commerceMap[commerceId] || normalizeCommerce(commerceId)
            return [
                commerceId,
                commerce.displayName,
                commerce.legalName,
                commerce.taxId,
                commerce.warehouseCode,
            ].some((field) => normalizeText(field).includes(search))
        })
    }, [commerceIds, commerceMap, searchTerm])

    const selectedCommerce = commerceMap[selectedId] || normalizeCommerce(selectedId)
    const selectedPrimaryLogo = selectedCommerce.primaryLogoUrl || commerceLogos[selectedCommerce.primaryLogoKey]
    const selectedSecondaryLogo = selectedCommerce.secondaryLogoUrl || commerceLogos[selectedCommerce.secondaryLogoKey]

    const loadCommerces = async () => {
        setLoading(true)
        try {
            const nextMap = {}
            const knownCommerceReads = await Promise.all(
                baseCommerceIds.map(async (commerceId) => {
                    try {
                        const snapshot = await getDoc(doc(db, 'Comercios', commerceId))
                        return {
                            id: commerceId,
                            data: snapshot.exists() ? snapshot.data() : {},
                        }
                    } catch (error) {
                        return {
                            id: commerceId,
                            data: {},
                        }
                    }
                })
            )

            knownCommerceReads.forEach(({ id, data }) => {
                nextMap[id] = normalizeCommerce(id, data)
            })

            const snapshot = await getDocs(collection(db, 'Comercios'))

            snapshot.forEach((snapshotDoc) => {
                nextMap[snapshotDoc.id] = normalizeCommerce(snapshotDoc.id, snapshotDoc.data())
            })

            setCommerceMap(nextMap)

            if (!nextMap[selectedId]) {
                setSelectedId(baseCommerceIds[0])
            }
        } catch (error) {
            const fallbackMap = {}
            const knownCommerceReads = await Promise.all(
                baseCommerceIds.map(async (commerceId) => {
                    try {
                        const snapshot = await getDoc(doc(db, 'Comercios', commerceId))
                        return {
                            id: commerceId,
                            data: snapshot.exists() ? snapshot.data() : {},
                        }
                    } catch (innerError) {
                        return {
                            id: commerceId,
                            data: {},
                        }
                    }
                })
            )

            knownCommerceReads.forEach(({ id, data }) => {
                fallbackMap[id] = normalizeCommerce(id, data)
            })

            setCommerceMap(fallbackMap)
            setSelectedId(baseCommerceIds[0])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadCommerces()
    }, [])

    useEffect(() => {
        setEditing(false)
    }, [selectedId])

    const startEditing = () => {
        setFormData(selectedCommerce)
        setBankAccountsText(stringifyBankAccounts(selectedCommerce.bankAccounts))
        setAdjustments(resolveOrderAdjustments(selectedCommerce.orderFormConfig))
        setMupCategories(selectedCommerce.orderFormConfig?.mupCategories || [])
        setEditing(true)
    }

    const cancelEditing = () => {
        setEditing(false)
        setFormData(selectedCommerce)
        setBankAccountsText(stringifyBankAccounts(selectedCommerce.bankAccounts))
        setAdjustments(resolveOrderAdjustments(selectedCommerce.orderFormConfig))
        setMupCategories(selectedCommerce.orderFormConfig?.mupCategories || [])
    }

    const handleAdjustmentChange = (index, field, value) => {
        setAdjustments((prev) => prev.map((adjustment, currentIndex) => (
            currentIndex === index
                ? { ...adjustment, [field]: value }
                : adjustment
        )))
    }

    const handleAddAdjustment = () => {
        setAdjustments((prev) => ([
            ...prev,
            {
                key: `Ajuste${prev.length + 1}`,
                label: '',
                operation: 'subtract',
                base: 'net',
                inputType: 'percent',
                enabled: true,
            },
        ]))
    }

    const handleRemoveAdjustment = (index) => {
        setAdjustments((prev) => prev.filter((_, currentIndex) => currentIndex !== index))
    }

    const handleCategoryChange = (index, field, value) => {
        setMupCategories((prev) => prev.map((category, currentIndex) => (
            currentIndex === index
                ? { ...category, [field]: value }
                : category
        )))
    }

    const handleAddCategory = () => {
        setMupCategories((prev) => ([
            ...prev,
            { value: '', label: '' },
        ]))
    }

    const handleRemoveCategory = (index) => {
        setMupCategories((prev) => prev.filter((_, currentIndex) => currentIndex !== index))
    }

    const handleChange = ({ target: { name, value } }) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleLogoUpload = async (slot, file) => {
        if (!file) return

        setLogoUploading((prev) => ({ ...prev, [slot]: true }))

        try {
            const logoUrl = await uploadCommerceLogo(file, selectedId, slot)
            const fieldName = slot === 'secondary' ? 'secondaryLogoUrl' : 'primaryLogoUrl'

            setFormData((prev) => ({
                ...prev,
                [fieldName]: logoUrl,
            }))
            ToastSucces('Logo cargado correctamente')
        } catch (error) {
            ToastError(`${error}`)
        } finally {
            setLogoUploading((prev) => ({ ...prev, [slot]: false }))
        }
    }

    const handleSave = async (e) => {
        e.preventDefault()
        setSaving(true)

        try {
            const bankAccounts = parseBankAccounts(bankAccountsText)
            const orderAdjustments = adjustments
                .map((adjustment, index) => ({
                    ...adjustment,
                    key: (adjustment.key || `Ajuste${index + 1}`).trim(),
                    label: (adjustment.label || adjustment.key || `Ajuste ${index + 1}`).trim(),
                }))
                .filter((adjustment) => adjustment.label)
            const normalizedMupCategories = mupCategories
                .map((category) => ({
                    value: (category.value || category.label || '').trim(),
                    label: (category.label || category.value || '').trim(),
                }))
                .filter((category) => category.value)
            const payload = {
                displayName: formData.displayName.trim(),
                legalName: formData.legalName.trim(),
                taxId: formData.taxId.trim(),
                warehouseCode: formData.warehouseCode.trim(),
                documentLabel: formData.documentLabel.trim(),
                documentPrefix: formData.documentPrefix.trim(),
                primaryLogoKey: formData.primaryLogoKey.trim(),
                secondaryLogoKey: formData.secondaryLogoKey.trim(),
                primaryLogoUrl: formData.primaryLogoUrl.trim(),
                secondaryLogoUrl: formData.secondaryLogoUrl.trim(),
                proformaValidityNote: formData.proformaValidityNote.trim(),
                legalNote: formData.legalNote.trim(),
                conditionsNote: formData.conditionsNote.trim(),
                bankAccounts,
                orderFormConfig: {
                    requiresFinalValidation: Boolean(formData.orderFormConfig?.requiresFinalValidation),
                    orderAdjustments,
                    mupCategories: normalizedMupCategories,
                    summary: {
                        ivaLabel: formData.orderFormConfig?.summary?.ivaLabel?.trim() || defaultOrderFormConfig.summary.ivaLabel,
                    },
                },
            }

            await Adddata(doc(db, 'Comercios', selectedId), payload)

            const updatedCommerce = normalizeCommerce(selectedId, payload)
            setCommerceMap((prev) => ({
                ...prev,
                [selectedId]: updatedCommerce,
            }))
            setEditing(false)
            ToastSucces('Comercio actualizado')
        } catch (error) {
            ToastError(`No se pudo guardar el comercio: ${error}`)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='p-2 mb-3'>
            <div className='mt-2'>
                <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                    <h4 className='separar my-4'>
                        <b className='text-uppercase'>Comercios</b>
                    </h4>
                    {!editing && (
                        <button
                            type='button'
                            className='btn btn-outline btnBlue d-inline-flex align-items-center gap-2'
                            onClick={startEditing}
                            disabled={loading}
                        >
                            <img src={editIco} alt='Editar comercio' className='icon20px' />
                            Editar
                        </button>
                    )}
                </div>

                <div className='adminPanelToolbar'>
                    <div className='adminSearch'>
                        <img src={searchIco} alt='Buscar' className='adminSearchIcon' />
                        <input
                            type='text'
                            className='form-control inputs adminSearchInput'
                            placeholder='Buscar comercio'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className='adminListMeta'>
                        {filteredCommerceIds.length} de {commerceIds.length} comercios
                    </div>
                </div>

                <div className='commerceAdminLayout'>
                    <aside className='commerceAdminSidebar'>
                        {filteredCommerceIds.map((commerceId) => {
                            const commerce = commerceMap[commerceId] || normalizeCommerce(commerceId)
                            const isActive = selectedId === commerceId

                            return (
                                <button
                                    key={commerceId}
                                    type='button'
                                    className={`commerceAdminTab ${isActive ? 'commerceAdminTab--active' : ''}`}
                                    onClick={() => setSelectedId(commerceId)}
                                >
                                    <span className='commerceAdminTabTitle'>
                                        {commerce.displayName || commerceId}
                                    </span>
                                    <span className='commerceAdminTabMeta'>
                                        {commerce.warehouseCode ? `Deposito ${commerce.warehouseCode}` : 'Sin deposito'}
                                    </span>
                                </button>
                            )
                        })}
                    </aside>

                    <section className='commerceAdminPanel'>
                        {loading ? (
                            <div className='adminEmptyState'>Cargando comercios...</div>
                        ) : !editing ? (
                            <div className='commerceAdminReadOnly'>
                                <div className='commerceAdminHeader'>
                                    <div>
                                        <h5 className='mb-1'>{selectedCommerce.displayName || selectedId}</h5>
                                        <div className='textColorGreyL small'>{selectedId}</div>
                                    </div>
                                </div>

                                <div className='commerceAdminGrid'>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Nombre visible</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.displayName || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Razon social</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.legalName || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>CUIT</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.taxId || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Deposito SAP</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.warehouseCode || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Etiqueta documento</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.documentLabel || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Prefijo documento</span>
                                        <span className='commerceInfoValue'>{selectedCommerce.documentPrefix || '-'}</span>
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Logo principal</span>
                                        {selectedPrimaryLogo ? (
                                            <img
                                                src={selectedPrimaryLogo}
                                                alt='Logo principal'
                                                className='commerceLogoThumb'
                                            />
                                        ) : (
                                            <span className='commerceInfoValue'>Sin logo</span>
                                        )}
                                    </div>
                                    <div className='commerceInfoCard'>
                                        <span className='commerceInfoLabel'>Logo secundario</span>
                                        {selectedSecondaryLogo ? (
                                            <img
                                                src={selectedSecondaryLogo}
                                                alt='Logo secundario'
                                                className='commerceLogoThumb'
                                            />
                                        ) : (
                                            <span className='commerceInfoValue'>Sin logo</span>
                                        )}
                                    </div>
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Vigencia de proforma</span>
                                    <p className='mb-0'>{selectedCommerce.proformaValidityNote || '-'}</p>
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Nota legal</span>
                                    <p className='mb-0'>{selectedCommerce.legalNote || '-'}</p>
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Condiciones comerciales</span>
                                    <p className='mb-0'>{selectedCommerce.conditionsNote || '-'}</p>
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Cuentas bancarias</span>
                                    {selectedCommerce.bankAccounts.length > 0 ? (
                                        <div className='commerceBankList'>
                                            {selectedCommerce.bankAccounts.map((account) => (
                                                <div key={`${account.bco}-${account.cc}-${account.cbu}`} className='commerceBankRow'>
                                                    <span className='commerceBankName'>{account.bco}</span>
                                                    <span>C/C {account.cc || '-'}</span>
                                                    <span>CBU {account.cbu || '-'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className='mb-0'>No hay cuentas cargadas.</p>
                                    )}
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Campos del pedido</span>
                                    <p className='mb-1'>
                                        <strong>Validacion final:</strong>{' '}
                                        {selectedCommerce.orderFormConfig?.requiresFinalValidation ? 'Requerida' : 'No requerida'}
                                    </p>
                                    <p className='mb-1'>
                                        <strong>Ajustes:</strong>{' '}
                                        {selectedCommerce.orderFormConfig?.orderAdjustments?.filter((adjustment) => adjustment.enabled !== false).map((adjustment) => adjustment.label).join(', ') || 'Sin ajustes'}
                                    </p>
                                    <p className='mb-0'>
                                        <strong>Categorias MUP:</strong>{' '}
                                        {selectedCommerce.orderFormConfig?.mupCategories?.map((category) => category.label || category.value).join(', ') || 'Sin categorias'}
                                    </p>
                                </div>

                                <div className='commerceTextBlock'>
                                    <span className='commerceInfoLabel'>Vista previa PDF</span>
                                    <CommercePreview commerce={selectedCommerce} />
                                </div>
                            </div>
                        ) : (
                            <form className='gerenciasCreateForm' onSubmit={handleSave}>
                                <div className='gerenciasEditInfo'>
                                    Editando comercio: {selectedCommerce.displayName || selectedId}
                                </div>

                                <div className='gerenciasCreateFields'>
                                    <input
                                        className='form-control inputs'
                                        name='displayName'
                                        placeholder='Nombre visible'
                                        value={formData.displayName}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='legalName'
                                        placeholder='Razon social'
                                        value={formData.legalName}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='taxId'
                                        placeholder='CUIT'
                                        value={formData.taxId}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='warehouseCode'
                                        placeholder='Deposito SAP'
                                        value={formData.warehouseCode}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='documentLabel'
                                        placeholder='Etiqueta documento'
                                        value={formData.documentLabel}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='documentPrefix'
                                        placeholder='Prefijo documento'
                                        value={formData.documentPrefix}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='primaryLogoKey'
                                        placeholder='Logo principal'
                                        value={formData.primaryLogoKey}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <input
                                        className='form-control inputs'
                                        name='secondaryLogoKey'
                                        placeholder='Logo secundario'
                                        value={formData.secondaryLogoKey}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div className='gerenciasCreateFields'>
                                    <div>
                                        <label className='form-label'>Subir logo principal</label>
                                        <input
                                            type='file'
                                            className='form-control inputs'
                                            accept='image/*'
                                            onChange={(e) => handleLogoUpload('primary', e.target.files?.[0])}
                                            disabled={saving || logoUploading.primary}
                                        />
                                        <small className='textColorGreyL'>
                                            {logoUploading.primary ? 'Subiendo logo principal...' : (formData.primaryLogoUrl || 'Sin logo principal cargado')}
                                        </small>
                                    </div>
                                    <div>
                                        <label className='form-label'>Subir logo secundario</label>
                                        <input
                                            type='file'
                                            className='form-control inputs'
                                            accept='image/*'
                                            onChange={(e) => handleLogoUpload('secondary', e.target.files?.[0])}
                                            disabled={saving || logoUploading.secondary}
                                        />
                                        <small className='textColorGreyL'>
                                            {logoUploading.secondary ? 'Subiendo logo secundario...' : (formData.secondaryLogoUrl || 'Sin logo secundario cargado')}
                                        </small>
                                    </div>
                                </div>

                                <div>
                                    <label className='form-label'>Vigencia de proforma</label>
                                    <textarea
                                        className='form-control inputs'
                                        rows='2'
                                        name='proformaValidityNote'
                                        value={formData.proformaValidityNote}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className='form-label'>Nota legal</label>
                                    <textarea
                                        className='form-control inputs'
                                        rows='4'
                                        name='legalNote'
                                        value={formData.legalNote}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className='form-label'>Condiciones comerciales</label>
                                    <textarea
                                        className='form-control inputs'
                                        rows='3'
                                        name='conditionsNote'
                                        value={formData.conditionsNote}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className='form-label'>Cuentas bancarias</label>
                                    <textarea
                                        className='form-control inputs'
                                        rows='5'
                                        value={bankAccountsText}
                                        onChange={(e) => setBankAccountsText(e.target.value)}
                                        disabled={saving}
                                    />
                                    <small className='textColorGreyL'>
                                        Un banco por linea con formato: `BANCO | CUENTA_CORRIENTE | CBU`
                                    </small>
                                </div>

                                <div>
                                    <label className='form-label'>Ajustes del pedido</label>
                                    <div className='d-flex flex-column gap-2'>
                                        {adjustments.map((adjustment, index) => (
                                            <div key={`${adjustment.key || 'ajuste'}-${index}`} className='row g-2 align-items-end'>
                                                <div className='col-md-3'>
                                                    <label className='form-label small'>Nombre</label>
                                                    <input
                                                        className='form-control inputs'
                                                        value={adjustment.label || ''}
                                                        onChange={(e) => handleAdjustmentChange(index, 'label', e.target.value)}
                                                        disabled={saving}
                                                        placeholder='DF'
                                                    />
                                                </div>
                                                <div className='col-md-2'>
                                                    <label className='form-label small'>Campo</label>
                                                    <input
                                                        className='form-control inputs'
                                                        value={adjustment.key || ''}
                                                        onChange={(e) => handleAdjustmentChange(index, 'key', e.target.value)}
                                                        disabled={saving}
                                                        placeholder='Descuento03'
                                                    />
                                                </div>
                                                <div className='col-md-2'>
                                                    <label className='form-label small'>Operacion</label>
                                                    <select
                                                        className='form-control inputs'
                                                        value={adjustment.operation || 'subtract'}
                                                        onChange={(e) => handleAdjustmentChange(index, 'operation', e.target.value)}
                                                        disabled={saving}
                                                    >
                                                        <option value='subtract'>Resta</option>
                                                        <option value='add'>Suma</option>
                                                    </select>
                                                </div>
                                                <div className='col-md-2'>
                                                    <label className='form-label small'>Base</label>
                                                    <select
                                                        className='form-control inputs'
                                                        value={adjustment.base || 'net'}
                                                        onChange={(e) => handleAdjustmentChange(index, 'base', e.target.value)}
                                                        disabled={saving}
                                                    >
                                                        <option value='net'>Neto</option>
                                                        <option value='subtotal'>Subtotal</option>
                                                        <option value='total'>Total</option>
                                                        <option value='product'>Por producto</option>
                                                    </select>
                                                </div>
                                                <div className='col-md-1'>
                                                    <label className='form-label small'>Tipo</label>
                                                    <select
                                                        className='form-control inputs'
                                                        value={adjustment.inputType || 'percent'}
                                                        onChange={(e) => handleAdjustmentChange(index, 'inputType', e.target.value)}
                                                        disabled={saving}
                                                    >
                                                        <option value='percent'>%</option>
                                                        <option value='amount'>$</option>
                                                    </select>
                                                </div>
                                                <div className='col-md-1'>
                                                    <label className='form-label small'>Activo</label>
                                                    <input
                                                        type='checkbox'
                                                        className='form-check-input ms-2'
                                                        checked={adjustment.enabled !== false}
                                                        onChange={(e) => handleAdjustmentChange(index, 'enabled', e.target.checked)}
                                                        disabled={saving}
                                                    />
                                                </div>
                                                <div className='col-md-1'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-outline-secondary w-100'
                                                        onClick={() => handleRemoveAdjustment(index)}
                                                        disabled={saving}
                                                    >
                                                        X
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <div>
                                            <button
                                                type='button'
                                                className='btn btn-outline btnBlue'
                                                onClick={handleAddAdjustment}
                                                disabled={saving}
                                            >
                                                Agregar ajuste
                                            </button>
                                        </div>
                                    </div>
                                    <small className='textColorGreyL'>
                                        Carga cada ajuste como fila. Ejemplo: `DF`, campo `Descuento03`, base `Neto`, tipo `%`.
                                    </small>
                                </div>

                                <div>
                                    <label className='form-label'>Categorias MUP</label>
                                    <div className='d-flex flex-column gap-2'>
                                        {mupCategories.map((category, index) => (
                                            <div key={`${category.value || 'mup'}-${index}`} className='row g-2 align-items-end'>
                                                <div className='col-md-5'>
                                                    <label className='form-label small'>Nombre</label>
                                                    <input
                                                        className='form-control inputs'
                                                        value={category.label || ''}
                                                        onChange={(e) => handleCategoryChange(index, 'label', e.target.value)}
                                                        disabled={saving}
                                                        placeholder='Calef'
                                                    />
                                                </div>
                                                <div className='col-md-5'>
                                                    <label className='form-label small'>Valor</label>
                                                    <input
                                                        className='form-control inputs'
                                                        value={category.value || ''}
                                                        onChange={(e) => handleCategoryChange(index, 'value', e.target.value)}
                                                        disabled={saving}
                                                        placeholder='Calef'
                                                    />
                                                </div>
                                                <div className='col-md-2'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-outline-secondary w-100'
                                                        onClick={() => handleRemoveCategory(index)}
                                                        disabled={saving}
                                                    >
                                                        X
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <div>
                                            <button
                                                type='button'
                                                className='btn btn-outline btnBlue'
                                                onClick={handleAddCategory}
                                                disabled={saving}
                                            >
                                                Agregar categoria
                                            </button>
                                        </div>
                                    </div>
                                    <small className='textColorGreyL'>
                                        Si no hay categorias, el pedido no las exigira.
                                    </small>
                                </div>

                                <div>
                                    <label className='form-label'>Validacion final antes de SAP</label>
                                    <div className='form-check form-switch'>
                                        <input
                                            type='checkbox'
                                            className='form-check-input'
                                            checked={Boolean(formData.orderFormConfig?.requiresFinalValidation)}
                                            onChange={(e) => setFormData((prev) => ({
                                                ...prev,
                                                orderFormConfig: {
                                                    ...(prev.orderFormConfig || {}),
                                                    requiresFinalValidation: e.target.checked,
                                                },
                                            }))}
                                            disabled={saving}
                                        />
                                        <label className='form-check-label'>
                                            {formData.orderFormConfig?.requiresFinalValidation ? 'Requiere validacion final antes de enviar a SAP' : 'No requiere validacion final'}
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className='form-label'>Etiqueta IVA en resumen</label>
                                    <input
                                        className='form-control inputs'
                                        name='ivaLabel'
                                        placeholder='IVA 21%'
                                        value={formData.orderFormConfig?.summary?.ivaLabel || ''}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            orderFormConfig: {
                                                ...(prev.orderFormConfig || {}),
                                                summary: {
                                                    ...(prev.orderFormConfig?.summary || {}),
                                                    ivaLabel: e.target.value,
                                                },
                                            },
                                        }))}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className='form-label'>Vista previa PDF</label>
                                    <CommercePreview
                                        commerce={{
                                            ...formData,
                                            id: selectedId,
                                            bankAccounts: parseBankAccounts(bankAccountsText),
                                        }}
                                    />
                                </div>

                                <div className='gerenciasActions'>
                                    <button type='submit' className='btn btn-outline btnBlue text-uppercase fw-bold' disabled={saving}>
                                        {saving ? 'Guardando...' : 'Guardar Comercio'}
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-outline-secondary text-uppercase fw-bold'
                                        onClick={cancelEditing}
                                        disabled={saving}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                </div>
            </div>
        </div>
    )
}
